import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
  tournamentScheduleApi,
  type TournamentSchedulePlan,
  type TournamentScheduleReview,
  type TournamentScheduleReviewCommand,
  type TournamentScheduleState,
} from "@bw/shared/api";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { TournamentScheduleWorkspace } from "./tournament-schedule-workspace";

type ReviewPageState = {
  plan: TournamentSchedulePlan;
  review: TournamentScheduleReview;
  schedule: TournamentScheduleState;
  venueTimeZone: string;
};

export function TournamentScheduleReviewPage() {
  const { id = "" } = useParams<{ id: string }>();
  const [state, setState] = useState<ReviewPageState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const loadEpoch = useRef(0);

  const load = useCallback(async () => {
    const epoch = ++loadEpoch.current;
    setLoading(true);
    setError(null);
    try {
      const [schedule, plans] = await Promise.all([
        tournamentScheduleApi.getTournamentScheduleState(id),
        tournamentScheduleApi.listTournamentSchedulePlans(id),
      ]);
      const draft = plans.find((candidate) => candidate.state === "draft");
      if (!draft) throw new Error("No draft order of play is awaiting review.");
      const [plan, review] = await Promise.all([
        tournamentScheduleApi.getTournamentSchedulePlan(id, draft.plan_id),
        tournamentScheduleApi.getTournamentSchedulePlanReview(id, draft.plan_id),
      ]);
      if (epoch !== loadEpoch.current) return;
      setState({
        plan,
        review,
        schedule,
        venueTimeZone: review.venue_timezone,
      });
    } catch (reason) {
      if (epoch !== loadEpoch.current) return;
      setError(
        reason instanceof Error
          ? reason.message
          : "The order of play could not be loaded.",
      );
    } finally {
      if (epoch === loadEpoch.current) setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
    return () => {
      ++loadEpoch.current;
    };
  }, [load]);

  if (loading) {
    return (
      <Stack direction="row" role="status" spacing={1} sx={{ p: 3 }}>
        <CircularProgress size={20} />
        <Typography>Loading order of play…</Typography>
      </Stack>
    );
  }
  if (error || !state) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Alert
          action={<Button onClick={() => void load()}>Retry</Button>}
          severity="error"
        >
          {error ?? "The order of play is unavailable."}
        </Alert>
      </Box>
    );
  }

  const reviewOnly = {
    ...state.review,
    can_plan: false,
    can_publish: false,
  };
  return (
    <Box sx={{ px: { xs: 2, lg: 3 }, py: 3 }}>
      <Stack spacing={2} sx={{ maxWidth: 1200, mx: "auto" }}>
        <Stack spacing={0.35}>
          <Typography component="h1" variant="h4">
            Review order of play
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Review the exact court and time assignments as the tournament referee.
            Planning and publication remain organizer responsibilities.
          </Typography>
        </Stack>
        {!state.review.required ? (
          <Alert severity="info">
            This tournament does not require BWF referee schedule approval.
          </Alert>
        ) : null}
        <TournamentScheduleWorkspace
          onPlanCommand={async () => {
            throw new Error("This is a review-only workspace.");
          }}
          onPreview={async () => {
            throw new Error("This is a review-only workspace.");
          }}
          onPublish={async () => {
            throw new Error("This is a review-only workspace.");
          }}
          onReview={async (command: TournamentScheduleReviewCommand) => {
            const review =
              await tournamentScheduleApi.reviewTournamentSchedulePlan(
                id,
                state.plan.plan_id,
                command,
              );
            setState((current) => (current ? { ...current, review } : current));
            return review;
          }}
          onRollback={async () => {
            throw new Error("This is a review-only workspace.");
          }}
          onUpdatePolicy={async () => {
            throw new Error("This is a review-only workspace.");
          }}
          plan={state.plan}
          publishedPlans={[]}
          review={reviewOnly}
          state={state.schedule}
          tournamentId={id}
          venueTimeZone={state.venueTimeZone}
        />
      </Stack>
    </Box>
  );
}
